import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import GoogleAuthButton from '../components/GoogleAuthButton';
import SiteLinks from '../components/SiteLinks';
import EmailVerificationCard from '../components/EmailVerificationCard';
import AuthBrand from '../components/AuthBrand';
import { APP_HOME, IS_MESSENGER } from '../config/appVariant';

const Login = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(() => {
    const fromQuery = new URLSearchParams(window.location.search).get('expired') === '1';
    const fromStorage = sessionStorage.getItem('bazzef_session_expired') === '1';
    if (fromStorage) {
      sessionStorage.removeItem('bazzef_session_expired');
    }
    return fromQuery || fromStorage;
  });
  const [verificationRequired, setVerificationRequired] = useState(false);
  const [verificationEmail, setVerificationEmail] = useState('');
  const [verificationMessage, setVerificationMessage] = useState('');

  const { login } = useAuth();
  const navigate = useNavigate();

  const handleAuthSuccess = () => {
    try {
      const pendingShare = sessionStorage.getItem('bazzef_pending_share');
      if (pendingShare) {
        sessionStorage.removeItem('bazzef_pending_share');
        navigate(IS_MESSENGER ? '/share' : '/compose', { state: { sharedText: pendingShare }, replace: true });
        return;
      }
    } catch (_) {}
    navigate(APP_HOME);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSessionExpired(false);
    setLoading(true);

    const result = await login(email, password);
    
    if (result.success) {
      handleAuthSuccess();
    } else {
      if (result.requireVerification) {
        setVerificationEmail(result.email || email);
        setVerificationMessage(result.error || '');
        setVerificationRequired(true);
      } else {
        setError(result.error);
      }
    }
    setLoading(false);
  };

  if (verificationRequired) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 dark:bg-black py-10 px-4 sm:px-6">
        <div className="max-w-sm w-full space-y-5 bg-white dark:bg-zinc-900 p-6 sm:p-7 rounded-2xl border border-zinc-200 dark:border-zinc-800 shadow-sm">
          <EmailVerificationCard
            email={verificationEmail}
            initialMessage={verificationMessage}
            onSuccess={handleAuthSuccess}
            onCancel={() => setVerificationRequired(false)}
          />
        </div>
        <SiteLinks className="mt-5 max-w-sm justify-center" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 dark:bg-black py-10 px-4 sm:px-6">
      <div className="max-w-sm w-full space-y-5 bg-white dark:bg-zinc-900 p-6 sm:p-7 rounded-2xl border border-zinc-200 dark:border-zinc-800 shadow-sm">
        <div>
          <AuthBrand />
          <h2 className="text-center text-lg font-semibold text-zinc-900 dark:text-zinc-100">
            {t('auth.login', 'Sign in to your account')}
          </h2>
          <p className="text-center text-xs text-zinc-500 dark:text-zinc-400 mt-0.5">
            {IS_MESSENGER
              ? 'Use your Bazzef account or Google.'
              : 'Welcome back! Please enter your details.'}
          </p>
        </div>

        {sessionExpired && !error && (
          <div className="flex items-center gap-2.5 bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-900/60 text-amber-700 dark:text-amber-400 px-3.5 py-2.5 rounded-xl text-xs font-medium">
            <i className="fas fa-clock-rotate-left text-sm shrink-0 text-amber-500" />
            <span>{t('auth.sessionExpired', 'Your session has expired. Please sign in again.')}</span>
          </div>
        )}

        {error && (
          <div className="flex items-center gap-2 bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900/60 text-red-600 dark:text-red-400 px-3.5 py-2.5 rounded-xl text-xs font-medium">
            <i className="fas fa-circle-exclamation text-sm shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {/* Social Authentication */}
        <div>
          <GoogleAuthButton
            mode="login"
            onSuccess={handleAuthSuccess}
            onError={(msg) => setError(msg)}
          />

          <div className="relative my-4">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-zinc-200 dark:border-zinc-800" />
            </div>
            <div className="relative flex justify-center text-[11px] uppercase tracking-wider font-medium">
              <span className="bg-white dark:bg-zinc-900 px-2.5 text-zinc-400 dark:text-zinc-500">
                {t('auth.orContinueWithEmail', 'or continue with email')}
              </span>
            </div>
          </div>
        </div>

        <form className="space-y-3.5" onSubmit={handleSubmit}>
          <div className="space-y-3">
            <div className="relative">
              <i className="far fa-envelope absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-400 text-sm pointer-events-none" />
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="appearance-none relative block w-full pl-10 pr-3.5 py-2.5 border border-zinc-200 dark:border-zinc-800 placeholder-zinc-400 dark:placeholder-zinc-500 text-zinc-900 dark:text-zinc-100 rounded-xl focus:outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 text-sm bg-white dark:bg-zinc-900 transition-all shadow-xs"
                placeholder="Email address"
              />
            </div>
            <div className="relative">
              <i className="fas fa-lock absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-400 text-sm pointer-events-none" />
              <input
                type={showPassword ? 'text' : 'password'}
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="appearance-none relative block w-full pl-10 pr-10 py-2.5 border border-zinc-200 dark:border-zinc-800 placeholder-zinc-400 dark:placeholder-zinc-500 text-zinc-900 dark:text-zinc-100 rounded-xl focus:outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 text-sm bg-white dark:bg-zinc-900 transition-all shadow-xs"
                placeholder="Password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-200 text-sm focus:outline-none p-1 transition-colors"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                <i className={showPassword ? 'far fa-eye-slash' : 'far fa-eye'} />
              </button>
            </div>
          </div>

          <div className="text-right rtl:text-left -mt-1">
            <Link to="/forgot-password" className="text-xs font-semibold text-sky-500 hover:text-sky-600">
              {t('account.forgotLink', 'Forgot password?')}
            </Link>
          </div>

          <div>
            <button
              type="submit"
              disabled={loading}
              className="group relative w-full flex justify-center py-2.5 px-4 border border-transparent text-sm font-semibold rounded-xl text-white dark:text-zinc-900 bg-zinc-900 dark:bg-zinc-100 hover:bg-zinc-800 dark:hover:bg-zinc-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-zinc-900 dark:focus:ring-zinc-100 disabled:opacity-50 transition-all shadow-sm active:scale-[0.99]"
            >
              {loading ? 'Signing in...' : 'Sign in'}
            </button>
          </div>

          <div className="text-center pt-1">
            <Link
              to="/register"
              className="text-xs text-zinc-500 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100 transition-colors"
            >
              Don't have an account? <span className="font-semibold text-sky-500 hover:text-sky-600">Sign up</span>
            </Link>
          </div>
        </form>
      </div>
      <SiteLinks className="mt-5 max-w-sm justify-center" />
    </div>
  );
};

export default Login;
